import { Fragment, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { AppUpdateNotice } from '@/features/app-update/AppUpdateNotice'
import { useEntryList } from '@/features/entries/queries'
import { readLinkOutcome, withoutLinkOutcome } from '@/features/login-method/outcome'
import { useEntryFormatters } from '@/features/language/use-entry-formatters'
import { useMessages } from '@/features/language/use-language'
import { Notice } from '@/shared/ui/Notice'
import { StatusMessage } from '@/shared/ui/StatusMessage'

/**
 * 첫 화면.
 *
 * 소셜 로그인이 계정을 이 왕복에서 처음 세웠다면 Backend가 돌아올 주소에 welcome을 붙여 보낸다.
 * 그 표시는 그 순간에만 뜻이 있어 첫 렌더에서 한 번 읽고 주소에서는 곧바로 지운다. 지우지 않으면
 * 새로고침마다 처음 온 사람 대접을 다시 받는다.
 *
 * 가입 직후 권유 화면에서 떠난 연결 왕복도 추가를 마치면 이 주소로 온다. 무엇이 붙었는지를
 * 목록 위의 한 줄이 한 번 알린다. 여기에는 추가가 어떻게 됐는지 보여 주는 것이 없어 아무 말도
 * 하지 않으면 사용자가 알아채지 못한다. 축하나 권유로 부풀리지 않고 일어난 일만 적는다.
 */
export function HomePage() {
  const m = useMessages()
  const { formatDate, formatTime } = useEntryFormatters()
  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useEntryList()
  const [searchParams, setSearchParams] = useSearchParams()
  const [welcomed] = useState(() => searchParams.get('welcome') === '1')
  const [outcome] = useState(() => readLinkOutcome(searchParams))
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    const cleaned = withoutLinkOutcome(searchParams) ?? new URLSearchParams(searchParams)
    if (!searchParams.has('welcome') && cleaned.toString() === searchParams.toString()) return
    cleaned.delete('welcome')
    setSearchParams(cleaned, { replace: true })
  }, [searchParams, setSearchParams])

  const entries = data?.pages.flatMap((page) => page.items) ?? []

  const added = !dismissed && outcome?.kind === 'added' ? outcome.provider : null

  return (
    <div className="flex flex-col gap-6">
      {/*
       * 목록 위 한 자리다. 한 번 보이고 닫으면 다시 뜨지 않는다. 주소에서 값을 지우므로
       * 새로고침으로도 다시 서지 않는다.
       */}
      {added && (
        <Notice action={{ label: m.link.close, onClick: () => setDismissed(true) }}>
          {m.link.addedNotice(m.auth.provider[added])}
        </Notice>
      )}

      <Link
        to="/entries/new"
        className="pressable bg-accent text-accent-ink border-accent-ink rounded-card flex min-h-12 items-center justify-center border px-5 text-center font-medium"
      >
        {m.entry.list.compose}
      </Link>

      {isPending && <StatusMessage>{m.entry.list.loading}</StatusMessage>}

      {isError && (
        <StatusMessage action={{ label: m.app.common.retry, onClick: () => void refetch() }}>
          {m.entry.list.failed}
        </StatusMessage>
      )}

      {!isPending && !isError && entries.length === 0 && (
        <>
          <StatusMessage>{welcomed ? m.entry.list.emptyWelcome : m.entry.list.empty}</StatusMessage>
          <AppUpdateNotice />
        </>
      )}

      {entries.length > 0 && (
        <ul className="flex flex-col gap-4">
          {entries.map((entry, index) => (
            <Fragment key={entry.id}>
              <li>
                <Link
                  to={`/entries/${entry.id}`}
                  className="bg-surface border-border/70 rounded-card block border px-5 py-4 transition-colors"
                >
                  <div className="text-ink-muted flex items-center gap-2 text-xs">
                    <span>{formatDate(entry.createdAt)}</span>
                    <span aria-hidden="true">·</span>
                    <span>{formatTime(entry.createdAt)}</span>
                  </div>
                  <p className="mt-2 line-clamp-3 leading-relaxed whitespace-pre-line">
                    {entry.content}
                  </p>
                </Link>
              </li>
              {/*
                알림은 첫 기록 카드 아래에 선다. 이 화면에서 먼저 읽힐 것은 기록이다.
                목록 항목이 아니라 별개 영역으로 두어 기록으로 읽히지 않게 한다.
              */}
              {index === 0 && (
                <div role="presentation">
                  <AppUpdateNotice />
                </div>
              )}
            </Fragment>
          ))}
        </ul>
      )}

      {hasNextPage && (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="text-ink-muted py-2 text-sm"
        >
          {isFetchingNextPage ? m.entry.list.loadingMore : m.entry.list.loadMore}
        </button>
      )}
    </div>
  )
}
